import Vue from "vue";
import ObjectModelBuilderModel from "../tracked/objectModelBuilderModel";
import ObjectModel from "../objectModel";
import {ObjectModelPropertyRootType, ObjectModelPropertyType} from "~/enums/objectModelPropertyType";
import Guid from "../../helpers/guid";
import GloballySelected from "../../../formability/object_model_builder/store/modules/selected";
import ObjectModelPropertyDefinition from "./objectModelPropertyDefinition";
import {ObjectModelPropertyCapability} from "./objectModelPropertyCapability";
import ObjectModelPropertyCapabilityManager from "./objectModelPropertyCapabilityManager";
import {
  IAggregatable,
  IAutoIncrementable,
  IDateMinMaxable,
  IDecimalPlaces,
  IFormattable,
  ILabelable,
  ILengthable,
  ILinkToRelationable,
  ILoggable,
  INumericMinMaxable,
  IOptionRangeRequirable,
  IReferenceable,
  IRequireable,
  ISelectable,
  ISortable,
} from "~/interfaces/propertyInterfaces";
import {InputFormatType} from "../../../formability/form_builder/models/inputFormatType";
import CharacterCount from "../../../formability/form_builder/models/characterCounts/characterCount";
import {RelationType} from "../relationType";
import CharacterCountFactory from "../../../formability/form_builder/models/characterCounts/characterCountFactory";
import {ObjectHelper} from "~/helpers/objectHelper";
import {Hash} from "~/interfaces/hashInterfaces";
import ObjectModelPropertyContainer from "../../mixins/objectModelPropertyContainer";
import numericValidationWithOptionalDecimalPlaces from "../../../formability/form_builder/helpers/numericValidationWithDecimalPlaces";
import {DatetimeMode} from "../datetimeMode";
import {TimeFormat} from "../timeFormat";
import {DatetimeType} from "../datetimeType";
import IRepo from "../../interfaces/repo";
import Repo from "../../state/modules/repo";
import ObjectModelRelation from "../objectModelRelations/objectModelRelation";
import ElementHandler from "../../services/elementHandler";
import IConditionalRuleReferencedControl from "../conditionalRules/referencedControl";
import {TargetType} from "../conditionalRules/targetType";
import {ComparisonType} from "../conditionalRules/comparisonType";
import {OptionRangeType} from "../../enums/optionRangeType";
import LinkedRelationMappedProperty from "./linkedRelationMappedProperty";
import {ArrayHelper} from "~/helpers/arrayHelper";
import {ObjectModelPropertyConditionalHelpers} from "../objectModels/objectModelPropertyConditionalHelpers";
import Validatable from "../../mixins/validatable";
import ObjectModelPropertyValidation from "../navigableValidations/objectModelPropertyValidation";
import {PickListSelectionType} from "~/enums/pickListSelectionType";
import {FileAttachmentType} from "~/enums/fileAttachmentType";
import {ObjectModelBuiltinPropertyTypeName} from "~/enums/objectModelBuiltinPropertyType";
import ConditionalRuleSetFactory from "../conditionalRules/conditionalRuleSetFactory";
import ObjectModelConditionalFields from "../objectModels/objectModelConditionalFields";
import {AggregateType} from "~/enums/aggregateType";
import {AccessPermission} from "~/enums/accessPermission";
import {AggregateNullValueType} from "~/enums/AggregateNullValueType";
import {IPermittee} from "~/interfaces/permitteeInterfaces";
import SecurityMaskableViewer from "~/models/securityMaskableViewer";
import {builtinDateIndexProperties, builtinTextIndexProperties, dateIndexProperties, IndexType, numericIndexProperties} from "~/enums/objectModelPropertyIndexType";
import {GenericType, IGenericTypeable} from '~/interfaces/genericTypeInterfaces'
import {IObjectModelProperty} from '~/interfaces/objectModelInterfaces'
import PropertySection from "~/models/objectModels/propertySection";
import SortableMixin from "~/mixins/sortableMixin";
import ObjectModelPermittable from "~/models/objectModelPermissions/objectModelPermittable";
import {ReferenceNodeType} from '~/enums/referenceNodeType'
import {ModelType} from '~/enums/modelType'
import {
  IImposter,
  IImposterFactory,
  IImposterMap,
  IImposterMapFactory,
  IImposterOptions
} from '~/interfaces/imposterInterfaces'
import Imposter from '~/models/imposter'
import DefaultImposterMapFactory from '~/models/imposterMapFactories/defaultImposterMapFactory'
import {commonImposterMap} from '~/models/imposterMapFactories/commonImposterMap'
import {IUniversalValue} from '~/interfaces/universalValueInterfaces'
import {InstanceHelper} from '~/helpers/instanceHelper'
import { IncrementalResetType } from '~/enums/incrementalResetType'
import { displayName, label } from '~/utils/objectModelPropertyUtils'

export default class ObjectModelProperty extends ObjectModelPermittable(SortableMixin(Validatable(ObjectModelPropertyContainer(ObjectModelBuilderModel)))) implements IObjectModelProperty, IConditionalRuleReferencedControl, ILabelable, IRequireable, IFormattable, ILengthable, ISortable, INumericMinMaxable, IDecimalPlaces, IDateMinMaxable, ILinkToRelationable, ISelectable, IAutoIncrementable, IOptionRangeRequirable, ILoggable, IReferenceable, IAggregatable, IGenericTypeable, IImposterFactory, IImposterMapFactory, IUniversalValue {
  rootModel: ObjectModel;
  property_type: ObjectModelPropertyType;
  mapped_property_type: ObjectModelPropertyType;
  mapped_property_datetime_mode: DatetimeMode;
  permissions: AccessPermission[];
  is_required: boolean;
  label_title: string;
  required_message: string;
  format_type: InputFormatType;
  format_message: string;
  format_pattern: string;
  character_count: CharacterCount;
  object_model_properties: Hash<ObjectModelProperty> = {}; // pick list property has properties
  sort_order: number;
  default_value: string | number;
  decimal_places: number;
  min_value: number;
  max_value: number;
  datetime_mode: DatetimeMode;
  max_datetime: string;
  max_datetime_type: DatetimeType;
  min_datetime: string;
  min_datetime_type: DatetimeType;
  default_datetime_type: DatetimeType;
  default_datetime: string;
  time_format: TimeFormat;
  linked_relation_client_id: string;
  linked_relation: ObjectModelRelation;
  increment_start: number;
  increment_reset_type: IncrementalResetType;
  derived_value: string;
  derived_value_formula: string;
  selection_type: PickListSelectionType;
  file_attachment_type: FileAttachmentType;
  option_range_type: OptionRangeType;
  checkbox_range_min: number;
  checkbox_range_max: number;
  is_logged: boolean;
  reference_name: string;
  _type: string;
  _objectModelPropertyHandler: ElementHandler = new ElementHandler(this, ObjectModelProperty, 'object_model_properties');
  linked_relation_mapped_properties: LinkedRelationMappedProperty[] = [];
  linked_property_has_null_value: boolean;
  null_value: AggregateNullValueType;
  null_replacement_value: number;
  aggregate_type: AggregateType;
  inverse: boolean;
  property_section_client_id: string;

  //----- SecurityMaskable

  security_maskable_enabled: boolean;
  security_maskable_partial_display_character_count: number;
  //// collection should be added to `EMBEDDED_MODELS` as:
  //// 'security_maskable_full_viewers': { type: SecurityMaskableViewer, relationType: RelationType.dictionary }
  //// 'security_maskable_partial_viewers': { type: SecurityMaskableViewer, relationType: RelationType.dictionary }
  security_maskable_full_viewers: { [key: string]: IPermittee } = {};
  security_maskable_partial_viewers: { [key: string]: IPermittee } = {};

  static getPropertyIndexType(property: ObjectModelProperty){
    let propertyType;
    if (property.property_type === ObjectModelPropertyRootType.MappedProperty){
      // if there are no linked relation mapped properties, then we don't know the type yet and return null
      if (!property.linked_relation_mapped_properties.length || !property.linked_relation_mapped_properties[0].linkedProperty) {
        // property type is a builtin

        // nothing is configured yet so just return null
        if (!property.linked_relation_mapped_properties.length) {
          return null;
        }

        propertyType = property.linked_relation_mapped_properties[0].default_property_type;
        // return index type here from builtin
        if (builtinTextIndexProperties.includes(propertyType)) {
          return IndexType.Keyword;
        } else if (builtinDateIndexProperties.includes(propertyType)) {
          return IndexType.Date;
        } else {
          return null;
        }
      } else {
        propertyType = property.linked_relation_mapped_properties[0].linkedProperty.property_type
      }
    }else{
      propertyType = property.property_type;
    }

    if (numericIndexProperties.includes(propertyType)){
      return IndexType.Double;
    }else if (dateIndexProperties.includes(propertyType)){
      return IndexType.Date;
    }else{
      return IndexType.Keyword;
    }
  }

  get is_security_maskable_enabled() : boolean {
    if (this.property_type === ObjectModelPropertyRootType.MappedProperty){
      return this.linked_relation_mapped_properties.some( x=> x.linked_property_cache?.security_maskable_enabled)
    }else{
      return this.security_maskable_enabled
    }
  }

  get securityMaskingFullViewersArray(): IPermittee[] {
    if (this.property_type === ObjectModelPropertyRootType.MappedProperty) {
      return this.linked_relation_mapped_properties.find( x=> x.linked_property_cache?.securityMaskingFullViewersArray?.length > 0)?.linked_property_cache?.securityMaskingFullViewersArray

    }else{
      return ObjectHelper.toArray(this.security_maskable_full_viewers);
    }
  }

  get securityMaskingPartialViewersArray(): IPermittee[] {
    if (this.property_type === ObjectModelPropertyRootType.MappedProperty) {
      return this.linked_relation_mapped_properties.find( x=> x.linked_property_cache?.securityMaskingPartialViewersArray?.length > 0)?.linked_property_cache?.securityMaskingPartialViewersArray
    } else {
      return ObjectHelper.toArray(this.security_maskable_partial_viewers);
    }
  }

  appendSecurityMaskableViewers(json, collectionNameOnParent: "security_maskable_full_viewers" | "security_maskable_partial_viewers"): IPermittee {
    const permittee = new SecurityMaskableViewer(this, json, collectionNameOnParent);
    this.trackCreationDeletion(permittee.client_id, null, permittee.asJson(), permittee);
    return permittee;
  }

  updateFormatAssociatedFields(format): void {
    if (format === InputFormatType.SocialSecurity) {
      this.security_maskable_enabled = true;
      this.security_maskable_partial_display_character_count = 4;
    }
  }

  //----- SecurityMaskable end

  get objectModelPropertyHandler(): ElementHandler<IObjectModelProperty> {
    return this._objectModelPropertyHandler;
  }

  except = ['objectModel', 'createEmbeddedObject', 'object_model_properties', 'character_count'];

  static repo: IRepo<ObjectModelProperty> = new Repo<ObjectModelProperty>('objectModelProperties', ObjectModelProperty);

  constructor() {
    super();
    this.defineProperty('client_id', Guid.create());
    this.defineProperty('inverse', null);
    this.defineProperty('property_section_client_id', null)
  }

  get EMBEDDED_MODELS(): { [key: string]: { type: any, relationType: RelationType } } {
    return {
      'object_model_properties': {type: ObjectModelProperty, relationType: RelationType.dictionary},
      'character_count': {type: CharacterCount, relationType: RelationType.singleObject},
      'linked_relation_mapped_properties': {type: LinkedRelationMappedProperty, relationType: RelationType.array},
      'security_maskable_full_viewers': { type: SecurityMaskableViewer, relationType: RelationType.dictionary },
      'security_maskable_partial_viewers': { type: SecurityMaskableViewer, relationType: RelationType.dictionary },
    };
  }

  get EMBEDDED_HASHES(): string[] {
    return ['character_count'];
  }

  createEmbeddedObject = {
    object_model_properties: (parent, json): ObjectModelProperty => {
      return new ObjectModelProperty();
    },
    character_count: (parent, json): CharacterCount => {
      return CharacterCountFactory.create(parent);
    },
    linked_relation_mapped_properties: (parent, json): LinkedRelationMappedProperty => {
      return LinkedRelationMappedProperty.repo.findOrCreate(json.id, json.client_id);
    },
    security_maskable_full_viewers: (parent, json): SecurityMaskableViewer<ObjectModelProperty> => {
      return new SecurityMaskableViewer(this, json, "security_maskable_full_viewers")
    },
    security_maskable_partial_viewers: (parent, json): SecurityMaskableViewer<ObjectModelProperty> => {
      return new SecurityMaskableViewer(this, json, "security_maskable_partial_viewers")
    }
  };

  initialize(root: ObjectModel, parent: ObjectModel | ObjectModelProperty = root, propertyType: ObjectModelPropertyType, json: object = {}): this {
    this.rootModel = root;
    this.parent = parent;
    this.defineProperty('property_type', propertyType);
    this.defineProperty('sort_order', 0);
    ObjectModelPropertyCapabilityManager.addDefaultProperties(this);
    this.loadFromJson(json);
    return this;
  }

  initializeEmbedded<T extends ObjectModelProperty | LinkedRelationMappedProperty>(parent: ObjectModel | ObjectModelProperty, item: T, position: number, json: object = {}) {
    return this.rootModel.initializeEmbedded(parent, item, position, json);
  }

  static get rootName(): string {
    return 'object_model_properties';
  }

  get rootName(): string {
    return 'object_model_properties';
  }

  // ******************** VALIDATABLE******************** //
  _validation: ObjectModelPropertyValidation = new ObjectModelPropertyValidation(this);

  getDisplayName(propName: string, value?): string {
    switch (propName) {
      case 'linked_relation_client_id':
        return 'Relationship';
      default:
        return propName;
    }
  }
  // ******************** VALIDATABLE END******************** //

  get isSelected(): boolean {
    return GloballySelected.objectModelProperty.is(this);
  }

  get icon(): string {
    return ObjectModelPropertyDefinition.getIcon(this.property_type);
  }

  static get displayName(): string {
    return 'ObjectModelProperty';
  }

  get displayName(): string {
    return displayName(this)
  }

  get controlName(): string {
    return this.displayName;
  }

  get elementType() {
    return this.property_type;
  }

  can(duty: ObjectModelPropertyCapability): boolean {
    return ObjectModelPropertyCapabilityManager.can(this.property_type, duty);
  }

  get objectModel(): ObjectModel {
    if (this.parent) {
      if (this.parent instanceof ObjectModel) {
        return this.parent
      } else if (this.parent instanceof LinkedRelationMappedProperty) {
        return this.parent.relatedObjectModel;
      } else {
        return this.parent.objectModel
      }
    }
  }

  get canHaveFormat(): boolean {
    return this.can(ObjectModelPropertyCapability.formattable);
  }

  get hasFormat(): boolean {
    return this.format_type && this.format_type > InputFormatType.None;
  }

  get hasPatternFormat(): boolean {
    return this.hasFormat && this.format_type === InputFormatType.Pattern;
  }

  get hasNumericFormat() {
    return this.hasFormat && this.format_type === InputFormatType.Numeric;
  }

  get mappedPropertyFormat(){
    if (this.property_type === ObjectModelPropertyRootType.MappedProperty){
      return this.linked_relation_mapped_properties[0]?.linkedProperty?.format_type
    }
  }

  get hasCurrencyFormat() {
    let effective_format;
    if (this.property_type === ObjectModelPropertyRootType.MappedProperty){
      effective_format = this.mappedPropertyFormat
    }else{
      effective_format = this.format_type
    }

    return this.hasFormat && effective_format === InputFormatType.Currency;
  }

  get isIndexable(): boolean {
    return this.can(ObjectModelPropertyCapability.indexable);
  }

  get isIndexed(): boolean {
    return this.objectModel && this.objectModel.isPropertyIndexed(this);
  }

  get objectModelPropertiesArray(): ObjectModelProperty[] {
    return ObjectHelper.toArray(this.object_model_properties);
  }

  get hasLabelTitle(): boolean {
    return this.label_title !== null && this.label_title.length > 0;
  }

  get canHaveCharacterCount(): boolean {
    return this.can(ObjectModelPropertyCapability.lengthable);
  }

  get hasCharacterCount(): boolean {
    return !!this.character_count;
  }

  get hasMinOrMaxLength(): boolean {
    return this.hasCharacterCount && this.character_count.hasMinOrMax;
  }

  get sortableParent(): ObjectModel | PropertySection | ObjectModelProperty {
    if (this.property_section_client_id) {
      return this.parent.findInProp('propertySectionsArray', this.property_section_client_id)
    } else {
      return this.parent
    }
  }

  get sortableHandler(): ElementHandler {
    return this.parent.objectModelPropertyHandler
  }

  cleanupDashboardViewRules() {
    for (const clientId in this.parent.dashboard_conditional_linked_detail_views) {
      if (this.parent.dashboard_conditional_linked_detail_views.hasOwnProperty(clientId)) {
        const linkedDetailView = this.parent.dashboard_conditional_linked_detail_views[clientId]
        const ruleSet = linkedDetailView.getSortedConditionalRuleSets('dashboard_conditional_linked_detail_view')
        if (ruleSet.length > 0) {
          if (ruleSet[0].getDependenciesFor(this.client_id).length > 0) {
            ruleSet[0].destroy()
            const newRuleSet = ConditionalRuleSetFactory.createForContainer(
              linkedDetailView,
              ObjectModelConditionalFields.DASHBOARD_CONDITIONAL_LINKED_DETAIL_VIEW,
              {},
              false,
              linkedDetailView
            );
            newRuleSet.attachToParent(linkedDetailView);
          }
        }
      }
    }
  }

  cleanupPropertyReferences(): void {
    this.objectModel.cleanupPropertyReferences(this);
  }

  afterDestroy() {
    super.afterDestroy();
    this.cleanupPropertyReferences();
    this.cleanupDashboardViewRules();
    this.parent.objectModelPropertyHandler.recalculateElementPositions();
  }

  defaultNumericValidation(): void {
    if (this.hasOwnProperty('default_value')) {
      this.default_value = numericValidationWithOptionalDecimalPlaces(this.default_value, this)
    }
  }

  get hasMinValue(): boolean {
    return this.can(ObjectModelPropertyCapability.numericMinMaxable) && this.min_value !== null
  }

  get hasMaxValue(): boolean {
    return this.can(ObjectModelPropertyCapability.numericMinMaxable) && this.max_value !== null
  }

  get hasMinOrMaxValue(): boolean {
    return this.hasMinValue || this.hasMaxValue;
  }

  get minMaxValueEqual(): boolean {
    return this.hasMinValue && this.hasMaxValue && this.min_value === this.max_value;
  }

  get valueRangeHTML(): string {
    let text = '';
    if (this.minMaxValueEqual) {
      text += this.min_value;
    } else {
      if (this.hasMinValue) {
        text += this.min_value;
      } else {
        text += `-${"&infin;"}`; // done this way to remove same between - and infin
      }
      text += ' - ';
      if (this.hasMaxValue) {
        text += this.max_value;
      } else {
        text += "&infin;";
      }
    }
    return text;
  }

  get canHaveDateMinMax(): boolean {
    return this.can(ObjectModelPropertyCapability.dateMinMaxable);
  }

  get hasDateMin(): boolean {
    return this.canHaveDateMinMax && this.min_datetime !== null && this.min_datetime !== undefined && this.min_datetime.length > 0;
  }

  get hasDateMax(): boolean {
    return this.canHaveDateMinMax && this.max_datetime !== null && this.min_datetime !== undefined && this.max_datetime.length > 0;
  }

  get hasDateMinOrMaxValue(): boolean {
    return this.hasDateMin || this.hasDateMax;
  }

  get canBeRequired(): boolean {
    return this.can(ObjectModelPropertyCapability.requireable);
  }

  get canAggregate(): boolean {
    return this.can(ObjectModelPropertyCapability.aggregatable);
  }

  addToRepo(): void {
    ObjectModelProperty.repo.add(this);
  }

  get modelCapabilities() {
    return ObjectModelPropertyCapability;
  }

  get selector(): typeof GloballySelected['objectModelProperty'] {
    return GloballySelected.objectModelProperty;
  }

  get isSelectable(): boolean {
    return this.can(this.modelCapabilities.selectable);
  }

  select(): void {
    if (this.isSelectable) {
      GloballySelected.propertySection.deselect()
      this.selector.set(this);
    }
  }

  deselect(): void {
    this.selector.deselectIf(this);
  }

  get linked_object_model_relation(): ObjectModelRelation {
    return ObjectModelRelation.repo.find(this.linked_relation_client_id)
  }

  get linked_object_model_relation_properties(): Array<ObjectModelProperty | LinkedRelationMappedProperty>{
    if (!this.linked_relation_mapped_properties) return [];
    let linkedObjectModelProperties = ObjectModelProperty.repo.all().filter(objectModelProperty => this.linked_relation_mapped_properties.some(
      (element) =>!element.default_property && element.property_client_id === objectModelProperty.client_id) );
    let linkedDefaultObjectModelProperties = this.linked_relation_mapped_properties.filter(property=> property.default_property && !!property.default_property_type )
    return [...linkedObjectModelProperties, ...linkedDefaultObjectModelProperties]
  }

  linked_relation_mapped_property(objectModelClientId: string): LinkedRelationMappedProperty {
    if (!this.linked_relation_mapped_properties) return;
    return this.linked_relation_mapped_properties.find(linkedRelationMappedProperty => linkedRelationMappedProperty.root_model_client_id === objectModelClientId)
  }

  get hasLinkedRelation(): boolean {
    return !!this.linked_object_model_relation;
  }

  get hasLinkedRelationProperties(): boolean {
    return this.linked_object_model_relation_properties.length > 0;
  }

  get linkedRelationLabelTitle(): string {
    if (this.hasLinkedRelation) {
      return this.linked_object_model_relation.labelTitleDisplay;
    } else {
      return 'No Relationship Selected';
    }
  }

  get linkedRelationPropertyLabelTitle(): string {
    if (this.hasLinkedRelationProperties) {
      return ArrayHelper.toSentence(this.linked_object_model_relation_properties, ', ', 'and', (model) => {
        if (model instanceof LinkedRelationMappedProperty){
          return ObjectModelBuiltinPropertyTypeName[model.default_property_type]
        }else{
          return model.labelTitleDisplay;
        }
      })
    } else {
      return 'No Property Selected';
    }
  }

  // is this needed for some reason?
  get hasConfiguredLinkedRelation(): boolean {
    if (this.can(ObjectModelPropertyCapability.linkToRelationPropertyable)) {
      return this.hasLinkedRelationProperties;
    } else if (this.can(ObjectModelPropertyCapability.linkToRelationable)) {
      return this.hasLinkedRelation;
    }
    return false;
  }

  get labelTitleDisplay(): string {
    if (this.label_title) {
      return this.label_title;
    }
    return 'Unnamed Property';
  }

  get defaultLabelTitle(): string {
    return label(this)
  }

// async await to handle asynchronous behavior of javascript
  async addNewLinkedPropertyRelation(json) {
    let newLinkedRelationMappedProperty = this.createEmbeddedObject.linked_relation_mapped_properties(this, json);
    await newLinkedRelationMappedProperty.initialize(this.rootModel, this, json);
    if (!this.linked_relation_mapped_properties) {
      this.linked_relation_mapped_properties = [];
    }
    this.initializeEmbedded(this, newLinkedRelationMappedProperty, this.linked_relation_mapped_properties.length + 1);
    let linkedRelationMappedPropertiesClone = [...this.linked_relation_mapped_properties];
    linkedRelationMappedPropertiesClone.push(newLinkedRelationMappedProperty);
    this.linked_relation_mapped_properties = Object.assign([], linkedRelationMappedPropertiesClone)
  }

  updateLinkedPropertyRelation(linkedPropertyRelation: LinkedRelationMappedProperty, props) {
    if (props.default_property){
      linkedPropertyRelation.property_client_id = "";
      linkedPropertyRelation.default_property = true;
      linkedPropertyRelation.default_property_type = props.client_id;
      Vue.set(linkedPropertyRelation, 'default_property', true)
      Vue.set(linkedPropertyRelation, 'default_property_type', linkedPropertyRelation.default_property_type)

    }else{
      linkedPropertyRelation.property_client_id = props.client_id;
      linkedPropertyRelation.default_property = false;
      linkedPropertyRelation.default_property_type= null;
      Vue.set(linkedPropertyRelation, 'default_property', false)
    }
  }

  clearAllLinkedObjectModelProperty(): void {
    if (this.linked_relation_mapped_properties) {
      this.linked_relation_mapped_properties.forEach(linkedRelationMappedProperty => linkedRelationMappedProperty.destroy());
    }
    this.linked_relation_mapped_properties = []
  }

  get referenceNameOrDefault(): string {
    return this.labelTitleDisplay;
  }

  is(...propertyTypes: Array<ObjectModelPropertyType>): boolean {
    return propertyTypes.includes(this.property_type) || propertyTypes.includes(this.mapped_property_type)
  }

  isNot(...propertyTypes: Array<ObjectModelPropertyType>): boolean {
    return !this.is(...propertyTypes);
  }

  get isValidForConditionalLogic(): boolean {
    if (this.is(ObjectModelPropertyRootType.MappedProperty)) {
      return !!this.linked_relation_client_id && this.linked_relation_mapped_properties.length > 0;
    }
    return true;
  }

  targetTypes(): TargetType[] {
    return ObjectModelPropertyConditionalHelpers.effectiveTargetTypes(this);
  }

  displayConditionType(targetType: TargetType): ComparisonType {
    return ObjectModelPropertyConditionalHelpers.effectiveComparisonType(this, targetType);
  }

  getChoiceComparisonOptions(): any[] {
    return ObjectModelPropertyConditionalHelpers.effectiveChoiceComparisonOptions(this);
  }

  getConditionDateTimeMode(): DatetimeMode {
    return ObjectModelPropertyConditionalHelpers.effectiveProperty(this).datetime_mode;
  }

  getConditionMinDateTime(): string {
    return ObjectModelPropertyConditionalHelpers.effectiveProperty(this).min_datetime;
  }

  getConditionMaxDateTime(): string {
    return ObjectModelPropertyConditionalHelpers.effectiveProperty(this).max_datetime;
  }

  get publishedEquivalent() {
    return this.objectModel.publishedEquivalent(this.client_id)
  }

  get builtinPropertyType(): null {
    return null
  }

  get isReadOnly(): boolean {
    return (this.permissions.length === 1) && (this.permissions[0] === AccessPermission.Read)
  }

  /**
   * Convenience getter to they have a common interface with IBuiltInPropertyValue
   */
  get default_property(): false {
    return false
  }

  get isUrl(): boolean {
    return (this.property_type === ObjectModelPropertyRootType.Text) &&
      (this.format_type === InputFormatType.Url);
  }

  get isUploader(): boolean {
    return this.is(ObjectModelPropertyRootType.FileAttachment);
  }

  get isPickList(): boolean {
    return this.is(ObjectModelPropertyRootType.PickList)
  }

  get isNumeric(): boolean {
    return this.genericType === ModelType.Number
  }

  get genericType(): GenericType {
    return ObjectModelPropertyDefinition.genericType(this)
  }

  get modelType(): ModelType.ObjectModelProperty {
    return ModelType.ObjectModelProperty
  }

  get referenceNodeType(): ReferenceNodeType {
    return ReferenceNodeType.ObjectModelProperty
  }

  /**
   * if the data of interest is itself, a collection of data
   */
  get isDataCollection(): boolean {
    return (this.isUploader && this.file_attachment_type === FileAttachmentType.MultipleFiles) ||
      this.isPickList && this.selection_type === PickListSelectionType.MultiSelect
  }

  imposterFor({ modelType, options }: { modelType: ModelType; options?: IImposterOptions }): IImposter {
    return new Imposter(this.imposterMapFor({ modelType, options }), options)
  }

  imposterMapFor({ modelType, options }: { modelType: ModelType; options?: IImposterOptions }): IImposterMap {
    let source: {modelType: ModelType, genericType: GenericType} = {modelType: this.modelType, genericType: this.genericType}
    if (this.format_type === InputFormatType.Email) {
      if (modelType === ModelType.User) {
        source.modelType = ModelType.User
      } else {
        source.modelType = ModelType.Email
      }
    }
    let map: IImposterMap = commonImposterMap({
      destination: {modelType, genericType: options.genericType},
      source,
      options,
    })
    if (!map) {
      map = new DefaultImposterMapFactory().imposterMapFrom(this)
    }
    const mapMusts: Partial<IImposterMap> = {
      isCollection: o => o.isCollection || this.isDataCollection,
    }

    return ObjectHelper.merge(
      map,
      mapMusts,
    )
  }

  /**
   * @param value {String} client_id of pick list option or text value for other property types
   * @param args
   */
  toUniversalValue(value: any, ...args: any[]) {
    if (this.isPickList) {
      return InstanceHelper.actOn(InstanceHelper.toArray(value), this.getLabel.bind(this))
    } else {
      return value
    }
  }

  /**
   * @param value {String} label_title of pick list option or text value for other property types
   */
  fromUniversalValue(value: any) {
    if (this.isPickList) {
      const getClientId = (labelTitle: string): string => this.objectModelPropertyHandler.findBy(p => p.label_title === labelTitle)?.client_id
      return InstanceHelper.actOn(InstanceHelper.toArray(value), getClientId)
    } else {
      return value
    }
  }

  getLabel(clientId: string): string {
    return this.objectModelPropertyHandler.find(clientId)?.defaultLabelTitle
  }
}
